import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";

import { ApiError } from "../lib/api.js";

/**
 * Nog een mailbox aanmelden, bijvoorbeeld die van een tweede bedrijf. Daarna
 * staan ze naast elkaar in deze app en wissel je via het accountmenu, zonder
 * uit te loggen. Elke mailbox houdt zijn eigen wachtwoord, mappen en
 * handtekening; een antwoord uit de Okidoky-mailbox gaat dus ook als Okidoky
 * de deur uit.
 */

interface Props {
  /** Standaard voor "aangemeld blijven": wat de gebruiker op dit apparaat al koos. */
  remembered: boolean;
  onAdd: (address: string, password: string, remember: boolean) => Promise<void>;
  onClose: () => void;
}

export function AddAccountDialog({ remembered, onAdd, onClose }: Props): ReactNode {
  const addressId = useId();
  const passwordId = useId();
  const [address, setAddress] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(remembered);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const opener = document.activeElement;
    inputRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape" && !busy) {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(address.trim(), password, remember);
      onClose();
    } catch (cause) {
      // Zelfde vage melding als op het inlogscherm; die nemen we letterlijk over.
      setError(cause instanceof ApiError ? cause.message : "Toevoegen is niet gelukt. Probeer het opnieuw.");
      setPassword("");
      setBusy(false);
    }
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true" aria-label="Mailbox toevoegen">
      <button type="button" className="sheet-dismiss" aria-label="Sluiten" onClick={onClose} disabled={busy} />

      <form className="sheet sheet--dialog" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="sheet-head">
          <h2 className="sheet-title">Mailbox toevoegen</h2>
        </div>

        <div className="sheet-body">
          <p className="field-hint add-account-intro">
            Meld nog een mailbox aan, bijvoorbeeld van een ander bedrijf. U wisselt daarna via het
            rondje rechtsboven; elke mailbox verstuurt met zijn eigen handtekening.
          </p>

          <div className="field">
            <label className="field-label" htmlFor={addressId}>Volledig e-mailadres</label>
            <input
              ref={inputRef}
              id={addressId}
              className="field-input"
              type="email"
              name="username"
              placeholder="naam@uwdomein.nl"
              // "off" en niet "username": anders vult de browser het adres in
              // waarmee al is ingelogd, en dat is hier juist niet de bedoeling.
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              required
              disabled={busy}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor={passwordId}>Wachtwoord van die mailbox</label>
            <input
              id={passwordId}
              className="field-input"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              disabled={busy}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <label className="login-remember">
            <input
              type="checkbox"
              checked={remember}
              disabled={busy}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span>
              Aangemeld blijven op dit apparaat
              <small>Niet aanvinken op een gedeelde of openbare computer.</small>
            </span>
          </label>

          {error !== null && <p className="composer-error" role="alert">{error}</p>}
        </div>

        <div className="sheet-foot sheet-foot--actions">
          <button type="button" className="button button--ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={busy || address.trim() === "" || password === ""}
          >
            {busy ? "Bezig…" : "Toevoegen"}
          </button>
        </div>
      </form>
    </div>
  );
}
